import { useEffect, useRef, useState } from "react";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";

// MUI
import { Stack, Typography } from "@mui/material";

// Types
import { ISelection, IOpCode } from "../../../types/sections/technicians";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";

const opCodeTypes: ISelection[] = [
	{ label: "Generic", value: "Generic" },
	{ label: "Quick Lube", value: "Quick Lube" },
	{ label: "GPS", value: "GPS" },
	{ label: "Tire", value: "Tire" },
	{ label: "Brake", value: "Brake" },
	{ label: "Drop Down O", value: "Drop Down O" },
];

type OpCodeProps = {
	selCode: string;
	selType: string;
	readOnly: boolean;
	index: number;
	onActionUpdate: ({
		code,
		type,
		pay,
		isValid,
		index,
	}: {
		code: string;
		type: string;
		pay: string[];
		isValid: boolean;
		index: number;
	}) => void;
	onActionDelete: (index: number) => void;
	removeDisabled: boolean;
	allOpCodes: IOpCode[];
	externalOpCodes: IOpCode[];
	payType: string[];
};

export default function OpCode(props: OpCodeProps) {
	const { selCode, selType, readOnly, index, onActionUpdate, onActionDelete, removeDisabled, allOpCodes, externalOpCodes, payType } =
		props;

	const [selectedCode, setSelectedCode] = useState<string>("");
	const [selectedType, setSelectedType] = useState<ISelection | undefined>();
	const [selectedPay, setSelectedPay] = useState<string[]>([]);
	const [repeatedCode, setRepeatedCode] = useState(false);
	const [external, setExternal] = useState<IOpCode[]>([]);

	const lastValue = useRef("");

	const checkExternal = ({
		code,
		type,
		pay,
		externalOC,
		multiple,
	}: {
		code: string;
		type: string;
		pay: string[];
		externalOC?: IOpCode[];
		multiple?: boolean;
	}) => {
		const useExt: IOpCode[] = externalOC || external;
		const margin: number = multiple ? 1 : 0;
		if (
			allOpCodes.filter((op) => op.code === code).length > margin ||
			useExt.filter((op) => op.code === code && op.type === type && op.pay?.length && op.pay.some((p) => pay.includes(p))).length >
				margin
		) {
			return false;
		}
		return true;
	};

	useEffect(() => {
		lastValue.current = selCode?.trim() ?? "";
		setSelectedCode(selCode?.trim() ?? "");
		setSelectedType(opCodeTypes.find((code: ISelection) => code.value === selType));
		setSelectedPay(payType?.length ? [...payType] : []);
		setExternal([...externalOpCodes]);
		setRepeatedCode(!checkExternal({ code: selCode ?? "", type: selType, pay: payType, externalOC: externalOpCodes, multiple: true }));
	}, [selCode, selType, payType, externalOpCodes]);

	useEffect(() => {
		if (index && allOpCodes.length) {
			setSelectedType({ label: allOpCodes[0].type, value: allOpCodes[0].type });
		}
	}, [allOpCodes]);

	const onFocusOutOpCode = () => {
		const value = selectedCode.trim();
		if (value !== selectedCode) {
			setSelectedCode(value);
		}
		if (lastValue.current !== value) {
			lastValue.current = value;
			const isValid = checkExternal({ code: value, type: selectedType?.value ?? "", pay: selectedPay });
			setRepeatedCode(!isValid);
			onActionUpdate({ code: value, type: selectedType?.value ?? "", pay: selectedPay, isValid, index });
		}
	};

	const onChangeOpCode = (value: string) => {
		setSelectedCode(value);
	};

	const onChangeCodeType = (value: any) => {
		setSelectedType(value as ISelection);
		const isValid = checkExternal({ code: selectedCode, type: value.value, pay: selectedPay });
		setRepeatedCode(!isValid);
		onActionUpdate({ code: selectedCode, type: value.value, pay: selectedPay, isValid, index });
	};

	function codeHasErrors() {
		return !selectedCode || repeatedCode;
	}

	return (
		<Stack direction={"row"}>
			<Stack mr={1.5}>
				<Typography className="!text-[14.5px] !font-sans" sx={{ color: "var(--text-color)" }}>
					Op Codes
				</Typography>

				<SimpleTextField
					editable
					outlined
					text={selectedCode}
					placeholder={"0"}
					onChange={(e) => onChangeOpCode(e)}
					onFocusOut={() => onFocusOutOpCode()}
					sx={{ width: "130px", mt: "5px" }}
					disabled={readOnly}
					classes={codeHasErrors() ? "!shadow-thin-red-line !bg-light-error" : ""}
				/>
			</Stack>

			<Stack>
				<Typography className="!text-[14.5px] !font-sans" sx={{ color: "var(--text-color)" }}>
					Code Type
				</Typography>
				<SmartSelector
					popperProps={{ className: "!z-[1350]" } as object}
					selectBy={"value"}
					showBy={"label"}
					options={opCodeTypes}
					disabled={readOnly || Boolean(index)}
					selected={selectedType}
					onChangeAction={(value) => onChangeCodeType(value as any)}
					error={!Boolean(selectedType)}
					sx={{ width: "130px", mt: "5px" }}
					inputProps={{
						placeholder: "Op Code Type",
						hideTooltip: true,
					}}
					tagsVariant="text"
				/>
			</Stack>

			<Stack sx={{ mt: "24px" }}>
				<SimpleIconButton
					className="btn-simple-remove !text-gray-400"
					ariaLabel="Remove"
					icon={<CloseRoundedIcon sx={{ fontSize: "22px" }} />}
					iconHover={<CloseRoundedIcon sx={{ fontSize: "22px" }} />}
					color={"var(--primary-color)"}
					sx={{ padding: "3px" }}
					loadingSize={20}
					sxLoading={{ padding: "2px" }}
					onClick={() => onActionDelete(index)}
					disabled={removeDisabled}
				/>
			</Stack>
		</Stack>
	);
}
